import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import Autoplay from "embla-carousel-autoplay";
import { Car, Timer } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { SaleTypeBadge } from "@/components/ui/SaleTypeBadge";
import { DescriptionPopup } from "@/components/auctions/DescriptionDialog";
import { indexLabels } from "@/i18n";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { useLanguage } from "@/context/LanguageContext";
import { NativeImage } from "@/components/native/NativeImage";
import { saveListingsScrollY } from "@/lib/native/listingsScrollRestore";
import { cn } from "@/lib/utils";

export type NativeCampaignSlide = {
  id: string;
  title: string;
  description: string;
  image: string;
  totalCars: number;
  isAuction: boolean;
  status: "active" | "upcoming" | "completed" | "cancelled";
  labels?: Array<{
    labelType: "reduced_percentage" | "reduced_amount" | "special";
    text: string;
  }>;
};

type NativeCampaignCarouselProps = {
  campaigns: NativeCampaignSlide[];
  isAuthenticated?: boolean;
  /** Wider slides / taller cards for web desktop */
  variant?: "native" | "web";
  /** Rendered under title + subtitle (e.g. filter pills) */
  filters?: ReactNode;
};

export const NativeCampaignCarousel = ({
  campaigns,
  isAuthenticated = false,
  variant = "native",
  filters,
}: NativeCampaignCarouselProps) => {
  const isWeb = variant === "web";
  const navigate = useNavigate();
  const { language } = useLanguage();
  const labels = indexLabels(language);
  const campaignLabels = auctionsLabels(language).campaignCard;
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const slideKey = campaigns.map((c) => c.id).join(",");
  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: 20000,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        playOnInit: campaigns.length > 1,
      }),
    // Recreate when slide set changes so Embla + autoplay stay in sync
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slideKey],
  );
  const [showDescription, setShowDescription] = useState<{
    title: string;
    description: string;
  } | null>(null);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setActiveIndex(api.selectedScrollSnap());
    api.on("select", onSelect);
    onSelect();
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api || campaigns.length <= 1) return;
    // Guard: autoplay can throw if Embla roots aren't ready yet after remount/filter
    try {
      const autoplay = api.plugins()?.autoplay as
        | { play?: () => void }
        | undefined;
      autoplay?.play?.();
    } catch {
      // ignore — playOnInit / next select will retry naturally
    }
  }, [api, campaigns.length, slideKey]);

  const handleClick = useCallback(
    (campaign: NativeCampaignSlide) => {
      if (campaign.status === "upcoming" && isAuthenticated) return;
      saveListingsScrollY("/auctions", "", window.scrollY);
      navigate(`/auctions/${campaign.id}`, {
        state: {
          from: "/auctions",
          fromScrollY: window.scrollY,
          nativeSlideIn: true,
        },
      });
    },
    [navigate, isAuthenticated],
  );

  const openDescription = (
    e: MouseEvent<HTMLButtonElement>,
    title: string,
    description: string,
  ) => {
    e.stopPropagation();
    setShowDescription({ title, description });
  };

  return (
    <section
      data-no-browse-swipe=""
      className={cn(
        "native-campaign-carousel",
        isWeb ? "mb-6" : "mb-2",
      )}
    >
      <div className={cn("text-center px-1", isWeb ? "mb-3" : "mb-2")}>
        <h2 className={cn("font-bold", isWeb ? "text-2xl" : "text-xl")}>
          {labels.campaigns.title}
        </h2>
        {labels.campaigns.subtitle ? (
          <p className="text-sm text-muted-foreground mt-1.5">
            {labels.campaigns.subtitle}
          </p>
        ) : null}
      </div>

      {filters ? (
        <div className="mb-3 flex justify-center">{filters}</div>
      ) : null}

      {campaigns.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {labels.campaigns.empty ?? "Inga kampanjer just nu"}
        </p>
      ) : (
      <Carousel
        key={slideKey}
        setApi={setApi}
        opts={{ align: "center", loop: campaigns.length > 1 }}
        plugins={campaigns.length > 1 ? [autoplayPlugin] : undefined}
        className="w-full"
      >
        <CarouselContent className={isWeb ? "-ml-4" : "-ml-3"}>
          {campaigns.map((group) => {
            const isUpcomingLocked =
              group.status === "upcoming" && isAuthenticated;
            const clickable = !isUpcomingLocked;

            return (
              <CarouselItem
                key={group.id}
                className={cn(
                  isWeb ? "pl-4 basis-[72%] md:basis-[48%] lg:basis-[38%]" : "pl-3 basis-[83%]",
                )}
              >
                <Card
                  className={cn(
                    "glass-card overflow-hidden border shadow-md cursor-pointer transition hover:shadow-lg",
                    clickable ? "active:scale-[0.99]" : "opacity-90",
                  )}
                  onClick={() => clickable && handleClick(group)}
                >
                  <div className={cn("relative", isWeb ? "h-52 md:h-56" : "h-44")}>
                    <NativeImage
                      src={group.image || "/placeholder.svg"}
                      alt={group.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
                    <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
                      {group.status === "upcoming" ? (
                        <Badge className="bg-blue-500/90 text-white flex items-center gap-1">
                          <Timer className="w-3 h-3" />
                          {labels.campaigns.upcoming}
                        </Badge>
                      ) : null}
                      <SaleTypeBadge
                        saleType={group.isAuction ? "auction" : "direct-purchase"}
                        className="text-[10px]"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-lg font-semibold text-white line-clamp-1">
                        {group.title}
                      </h3>
                      {group.labels && group.labels.length > 0 ? (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {group.labels.map((label, index) => {
                            const labelConfig = {
                              reduced_percentage: {
                                color:
                                  "bg-red-500/90 text-white border-red-400/40",
                                icon: "%",
                              },
                              reduced_amount: {
                                color:
                                  "bg-emerald-500/90 text-white border-emerald-400/40",
                                icon: "kr",
                              },
                              special: {
                                color:
                                  "bg-sky-500/90 text-white border-sky-400/40",
                                icon: "★",
                              },
                            }[label.labelType];

                            return (
                              <Badge
                                key={`${group.id}-label-${index}`}
                                variant="secondary"
                                className={cn(
                                  "text-[10px] px-1.5 py-0.5 border font-medium",
                                  labelConfig.color,
                                )}
                              >
                                {labelConfig.icon} {label.text}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : null}
                      {group.description ? (
                        <div className="mt-1 flex items-baseline gap-1 min-w-0">
                          <p className="text-xs text-white/80 line-clamp-1 flex-1 min-w-0">
                            {group.description}
                          </p>
                          <button
                            type="button"
                            className="shrink-0 text-[11px] font-semibold text-amber-300/95 hover:text-amber-200 transition-colors"
                            onClick={(e) =>
                              openDescription(
                                e,
                                group.title,
                                group.description,
                              )
                            }
                          >
                            {campaignLabels.showMore}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-black/90 flex items-center justify-between text-white/85">
                    <div className="flex items-center gap-2 text-sm">
                      <Car className="w-4 h-4" />
                      <span>
                        {labels.campaigns.listingsCount.replace(
                          "{count}",
                          String(group.totalCars),
                        )}
                      </span>
                    </div>
                    {clickable && (
                      <span className="text-sm font-medium text-yellow-400">
                        {labels.campaigns.seeAll}
                      </span>
                    )}
                  </div>
                </Card>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      )}

      {campaigns.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {campaigns.map((c, i) => (
            <button
              key={c.id}
              type="button"
              aria-label={`Kampanj ${i + 1}`}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                i === activeIndex
                  ? "w-5 bg-primary"
                  : "w-1.5 bg-muted-foreground/40",
              )}
              onClick={() => api?.scrollTo(i)}
            />
          ))}
        </div>
      )}

      {showDescription && (
        <DescriptionPopup
          description={showDescription.description}
          title={showDescription.title}
          onClose={() => setShowDescription(null)}
        />
      )}
    </section>
  );
};
